import { PathLayer, ScatterplotLayer } from '@deck.gl/layers';

const isValidCoordinate = (coordinate) => {
	if (!Array.isArray(coordinate) || coordinate.length < 2) {
		return false;
	}

	const longitude = Number(coordinate[0]);
	const latitude = Number(coordinate[1]);

	return (
		Number.isFinite(longitude) &&
		Number.isFinite(latitude) &&
		longitude >= -180 &&
		longitude <= 180 &&
		latitude >= -90 &&
		latitude <= 90
	);
};

/**
 * Normalise every shape that the map can receive into
 * a flat Feature[] array.
 */
const normaliseFeatures = (links) => {
	if (!links) return [];

	// Direct FeatureCollection
	if (links.type === 'FeatureCollection') {
		return Array.isArray(links.features) ? links.features.filter(Boolean) : [];
	}

	// Direct Feature
	if (links.type === 'Feature') {
		return links.geometry ? [links] : [];
	}

	// { features: [...] }
	if (Array.isArray(links.features)) {
		return links.features.filter(Boolean);
	}

	// { data: [...] }
	if (Array.isArray(links.data)) {
		return normaliseFeatures(links.data);
	}

	// { links: [...] }
	if (Array.isArray(links.links)) {
		return normaliseFeatures(links.links);
	}

	// Array of Features / FeatureCollections
	if (Array.isArray(links)) {
		return links.flatMap((item) => normaliseFeatures(item));
	}

	return [];
};

const createPointFeature = (feature, coordinates, featureIndex, extraProperties = {}) => {
	if (!isValidCoordinate(coordinates)) {
		return null;
	}

	return {
		...feature,

		geometry: {
			type: 'Point',
			coordinates: [Number(coordinates[0]), Number(coordinates[1])],
		},

		properties: {
			...(feature.properties || {}),
			...extraProperties,

			_networkFeatureIndex: featureIndex,
			_networkOriginalGeometryType: feature.geometry?.type,
		},
	};
};

const createNetworkPoint = (
    feature,
    featureIndex,
    coordinates,
    pointType,
    segmentIndex = null
) => {
    if (!isValidCoordinate(coordinates)) {
        return null;
    }

    const sourceProperties = feature.properties || {};

    let name;

    if (pointType === 'link-start') {
        name =
            sourceProperties.INLETNODE ??
            sourceProperties.inletNode ??
            sourceProperties.inlet_node;
    }

    if (pointType === 'link-end') {
        name =
            sourceProperties.OUTLETNODE ??
            sourceProperties.outletNode ??
            sourceProperties.outlet_node;
    }

    return {
        type: 'Feature',

        geometry: {
            type: 'Point',
            coordinates: [
                Number(coordinates[0]),
                Number(coordinates[1]),
            ],
        },

        properties: {
            type: 'node',
            name: name || 'Network node',

            nodeType:
                pointType === 'link-start'
                    ? 'inlet'
                    : pointType === 'link-end'
                        ? 'outlet'
                        : 'node',

            _networkPointType: pointType,
            _networkFeatureIndex: featureIndex,
            _networkGeometryType: feature.geometry?.type,

            // Preserve the original link properties
            // without making them part of the node identity.
            _networkSourceProperties: sourceProperties,

            ...(segmentIndex !== null
                ? {
                    _segment: segmentIndex,
                }
                : {}),
        },
    };
};

const generateInfrastructurePaths = (links, zIndex) => {
	const features = normaliseFeatures(links);

	if (!features.length) {
		console.warn('[Network] No network features found');
		return [];
	}

	const lineFeatures = [];
	const pointFeatures = [];

	features.forEach((feature, featureIndex) => {
		const geometry = feature?.geometry;

		if (!geometry?.type) {
			return;
		}

		switch (geometry.type) {
			/*
			 * -----------------------------------------
			 * POINT
			 * -----------------------------------------
			 */
			case 'Point': {
				const point = createPointFeature(feature, geometry.coordinates, featureIndex, {
					_networkPointType: 'node',
				});

				if (point) {
					pointFeatures.push(point);
				}

				break;
			}

			/*
			 * -----------------------------------------
			 * MULTIPOINT
			 * -----------------------------------------
			 */
			case 'MultiPoint': {
				(geometry.coordinates || []).forEach((coordinates, pointIndex) => {
					const point = createPointFeature(feature, coordinates, featureIndex, {
						_networkPointType: 'node',
						_segment: pointIndex,
					});

					if (point) {
						pointFeatures.push(point);
					}
				});

				break;
			}

			/*
			 * -----------------------------------------
			 * LINESTRING
			 * -----------------------------------------
			 */
			case 'LineString': {
				const coordinates = geometry.coordinates;

				if (!Array.isArray(coordinates) || coordinates.length < 2) {
					break;
				}

				lineFeatures.push(feature);

				/*
				 * Add the start and end of every link as
				 * network node points.
				 */
				const startPoint = createNetworkPoint(feature, featureIndex, coordinates[0], 'link-start');

				const endPoint = createNetworkPoint(
					feature,
					featureIndex,
					coordinates[coordinates.length - 1],
					'link-end',
				);

				if (startPoint) {
					pointFeatures.push(startPoint);
				}

				if (endPoint) {
					pointFeatures.push(endPoint);
				}

				break;
			}

			/*
			 * -----------------------------------------
			 * MULTILINESTRING
			 * -----------------------------------------
			 */
			case 'MultiLineString': {
				(geometry.coordinates || []).forEach((coordinates, segmentIndex) => {
					if (!Array.isArray(coordinates) || coordinates.length < 2) {
						return;
					}

					const lineFeature = {
						...feature,

						geometry: {
							type: 'LineString',
							coordinates,
						},

						properties: {
							...(feature.properties || {}),
							_segment: segmentIndex,
						},
					};

					lineFeatures.push(lineFeature);

					const startPoint = createPointFeature(
						feature,
						featureIndex,
						coordinates[0],
						'link-start',
						segmentIndex,
					);

					const endPoint = createPointFeature(
						feature,
						featureIndex,
						coordinates[coordinates.length - 1],
						'link-end',
						segmentIndex,
					);

					if (startPoint) {
						pointFeatures.push(startPoint);
					}

					if (endPoint) {
						pointFeatures.push(endPoint);
					}
				});

				break;
			}

			default:
				console.warn('[Network] Unsupported geometry:', geometry.type, feature);
		}
	});

	const layers = [];

	/*
	 * =========================================
	 * NETWORK LINKS
	 * =========================================
	 */
	if (lineFeatures.length > 0) {
		const lineLayer = new PathLayer({
			id: 'infrastructure-links',
			data: lineFeatures,

			getPath: (feature) =>
				feature.geometry.coordinates.map(([lng, lat]) => [Number(lng), Number(lat)]),
			getColor: [30, 150, 190, 255],
			jointRounded: true,
			capRounded: true,
			widthMinPixels: 1,
			widthMaxPixels: 4,
			pickable: true,
		});

		lineLayer.zIndex = zIndex;

		layers.push(lineLayer);
	}

	/*
	 * =========================================
	 * NETWORK NODES
	 * =========================================
	 */
	if (pointFeatures.length > 0) {
		const nodeLayer = new ScatterplotLayer({
			id: 'infrastructure-nodes',
			data: pointFeatures,
			getPosition: (feature) => feature.geometry.coordinates,
			radiusUnits: 'pixels',
			radiusMinPixels: 5,
			radiusMaxPixels: 8,
			getFillColor: [160, 61, 204, 220],
			getLineColor: [255, 255, 255, 220],
			getLineWidth: 1,
			lineWidthUnits: 'pixels',
			stroked: true,
			pickable: true,
		});

		nodeLayer.zIndex = zIndex;

		layers.push(nodeLayer);
	}

	return layers;
};

export default generateInfrastructurePaths;